import { styles } from "@repo/helpers";
import { Fragment } from "react";

import { Button, ButtonTypes } from "../basic/button";
import { ButtonGroup, ButtonGroupOption } from "../basic/buttonGroup";
import { Skeleton } from "../basic/skeleton";
import { Spinner } from "../basic/spinner";
import { Header } from "./header";
import { ButtonColumn, Column } from "./models";
import Pagination from "./pagination";
import style from "./style.module.scss";
import ToggleColumnsDropdown from "./toggleColumnsDropdown";
import { GridMarkupProps } from "./types";

/**
 * Get the grid markup
 * @param props
 */
export default function GridMarkup<T>(props: GridMarkupProps<T>) {
	const {
		id,
		tableClassName,
		containerClassName,
		tableContainerClassName,
		currentPage,
		totalItems,
		pageSize,
		title,
		loading,
		setItemsPerPage,
		hidePagination,
		pageChanged,
		columns,
		columnOrder,
		setColumnOrder,
	} = props;

	const tbody = renderBody();
	return (
		<>
			<div
				className={styles(style.gridContainer, containerClassName)}
				style={
					props.maxHeight
						? {
								maxHeight: props.maxHeight,
							}
						: undefined
				}
			>
				{title && (
					<div className={style.title}>
						<div className={styles(style.sideContainer, style.left)}>
							<LeftActionsContainer {...props} />
						</div>
						<h4>{title}</h4>
						<div className={styles(style.sideContainer, style.right)}>
							<RightActionsContainer {...props} />
						</div>
					</div>
				)}
				<div
					className={styles(style.tableContainer, tableContainerClassName)}
					id={id}
					style={
						props.maxHeight
							? {
									flex: 1,
									overflowY: "auto",
								}
							: undefined
					}
				>
					<div className={style.tableOverlay}>&nbsp;</div>
					<table className={styles(style.table, tableClassName)} ref={props.tableRef}>
						<Header<T>
							{...props}
							columns={columns}
							columnOrder={columnOrder}
							setColumnOrder={setColumnOrder}
						/>
						{tbody}
					</table>
				</div>
				{!hidePagination && (
					<Pagination
						currentPage={currentPage}
						totalItems={totalItems}
						pageChanged={pageChanged}
						setItemsPerPage={setItemsPerPage}
						itemsPerPage={pageSize}
						refresh={props.hideRefresh ? undefined : props.refresh}
						canSetItemsPerPage={props.canSetItemsPerPage}
						pageSizes={props.pageSizes}
					/>
				)}
			</div>
			<SettingsContainer {...props} />
		</>
	);

	/**
	 * Renders a loading state for a table with skeleton rows and cells.
	 * This is intended to display a placeholder while data is being loaded.
	 */
	function renderLoading() {
		const { rowClassName, cellClassName } = props;

		const skeletonRows = Array.from({ length: pageSize ?? 10 });
		return (
			<tbody>
				{skeletonRows.map((_, rowIndex) => (
					<tr className={styles(style.tableRow, rowClassName)} key={`skeleton-row-${rowIndex}`}>
						{columns.map((column, colIndex) => (
							<td
								className={styles(
									style.tableCell,
									style.loadingCell,
									cellClassName,
									!column.visible && style.hidden,
								)}
								key={`skeleton-cell-${rowIndex}-${colIndex}`}
							>
								<Skeleton />
							</td>
						))}
					</tr>
				))}
			</tbody>
		);
	}

	/**
	 * Get the table body for the grid
	 */
	function renderBody() {
		const { values, cellClassName, rowClassName, keyProperty, onClickRow, expandedDisplay, selected } = props;

		if (loading) {
			return renderLoading();
		}

		function onClick(id: any) {
			if (onClickRow) return () => onClickRow(id);
			else return () => {};
		}

		return <tbody>{values.map(renderRow)}</tbody>;

		function renderRow(val: T) {
			const keyVal = val[keyProperty] || JSON.stringify(val);
			const subDisplay = expandedDisplay && expandedDisplay(keyVal);
			if (subDisplay)
				return (
					<Fragment key={String(keyVal)}>
						{render()}
						<tr
							className={styles(
								style.tableRow,
								rowClassName,
								selected?.includes(keyVal as string | number) && style.selected,
							)}
						>
							<td colSpan={columns.length}>{subDisplay}</td>
						</tr>
					</Fragment>
				);
			return render();

			function render() {
				return (
					<tr
						className={styles(
							style.tableRow,
							rowClassName,
							selected?.includes(keyVal as string | number) && style.selected,
						)}
						key={String(keyVal)}
						onClick={onClick(keyVal)}
					>
						{columns.map(renderCell)}
					</tr>
				);
			}

			/**
			 * Get a cell for the table
			 */
			function renderCell(column: Column<T>, index: number) {
				const content = column.getValue(val);

				if (props.isEnriching && (content === undefined || content === null || content === "")) {
					return (
						<td
							className={styles(style.tableCell, cellClassName, !column.visible && style.hidden)}
							key={index}
						>
							<Skeleton />
						</td>
					);
				}

				let strContent = (content || "") as string;
				let overranCharLimit = false;
				if (column.charLimit) {
					strContent = strContent.toString();
					overranCharLimit = column.charLimit && strContent.length > column.charLimit;
					if (overranCharLimit) {
						strContent = strContent.slice(0, column.charLimit) + "[...]";
					}
				}
				let cellContent;
				if (column instanceof ButtonColumn) {
					cellContent = content ? (
						<Button
							types={[ButtonTypes.Primary]}
							color={column.color}
							onClick={() => column.onClick?.(val)}
						>
							{column.buttonText || strContent}
						</Button>
					) : null;
				} else {
					cellContent = column.linkUrl ? (
						<a
							target="_blank"
							rel="noreferrer"
							href={column.linkUrl.replace(`{${column.propertyName}}`, strContent)}
						>
							{strContent}
						</a>
					) : (
						strContent
					);
				}
				return (
					<td
						className={styles(style.tableCell, cellClassName, !column.visible && style.hidden)}
						key={index}
						title={(overranCharLimit && content) || null}
					>
						{cellContent}
					</td>
				);
			}
		}
	}
}

export function LeftActionsContainer<T>(props: GridMarkupProps<T>) {
	return (
		<div className="flex gap-2">
			{props.onCreateClick ? (
				<Button types={[ButtonTypes.Green]} onClick={props.onCreateClick}>
					{props.createButtonText ?? "New"}
				</Button>
			) : null}
			{props.additionalActions}
		</div>
	);
}

export function RightActionsContainer<T>(props: GridMarkupProps<T>) {
	return (
		<div className="flex gap-2">
			{props.exportable ? (
				<>
					<ButtonGroup
						options={getExportOptions(props)}
						selectedId={"excel"}
						onButtonClick={(opt) => {
							if (opt.id === "excel") props.excelExportEvent?.();
							else if (opt.id === "csv") props.csvExportEvent?.();
						}}
						types={[ButtonTypes.Green]}
					/>
				</>
			) : null}
		</div>
	);

	function getExportOptions(p: GridMarkupProps<T>): ButtonGroupOption[] {
		const excelLabel = p.exportDisabled ? (
			<Spinner tag="span" right>
				Exporting...
			</Spinner>
		) : (
			"Export Excel"
		);
		const csvLabel = p.csvExportDisabled ? (
			<Spinner tag="span" right>
				Exporting...
			</Spinner>
		) : (
			"Export CSV"
		);

		if (!p.exportable) return [];

		return [
			{ id: "excel", label: excelLabel, disabled: p.exportDisabled },
			{ id: "csv", label: csvLabel, disabled: p.csvExportDisabled },
		];
	}
}

export function SettingsContainer<T>(props: GridMarkupProps<T>) {
	return (
		<div className={style.settingsContainer}>
			{props.changeVisibility ? (
				<ToggleColumnsDropdown columns={props.columns} updateVisibility={props.updateColumnVisibility} />
			) : null}
			{props.stateful ? (
				<>
					<Button onClick={props.saveGridConfiguration}>Save Report Settings</Button>
					<Button onClick={props.clearGridConfiguration}>Clear Saved Report Settings</Button>
				</>
			) : null}
		</div>
	);
}
